"use client"

import * as Sentry from "@sentry/nextjs"
import Script from "next/script"
import { useEffect, useRef, useState } from "react"
import { preconnect, preload } from "react-dom"

/**
 * Fillout form embed using the official "standard" script-based pattern.
 *
 * This is the same pattern that @fillout/react's FilloutStandardEmbed wraps
 * — we inline it here to avoid a new dep (we can swap to @fillout/react
 * later with a one-component change). The Framer site embeds Fillout this
 * same way.
 *
 * Features:
 * - `dynamicResize` (default true) — form auto-resizes to its content
 * - `inheritParameters` (default true) — forwards UTM / query params from
 *   the host page URL into the form (attribution survives into submissions)
 * - `domain` — loads the form from our white-labeled forms.ahead.clinic
 *
 * The Fillout embed script runs a one-time DOM scan on load (no
 * MutationObserver). It marks processed divs with `data-fillout-initialized`
 * and has no public re-init API. When a component remounts (e.g. modal
 * close→reopen, SPA navigation), we re-execute the script so it picks up
 * the new uninitialized div.
 *
 * Loading state: shows a scheduler-shaped skeleton (calendar grid + time
 * slot list) while the Fillout iframe is loading, so the user sees structure
 * instead of a blank white panel during the multi-second cold start. The
 * skeleton fades out as soon as the iframe `load` event fires.
 *
 * Used by /free-call and /contact (Fillout modal).
 */

const FILLOUT_SCRIPT_SRC = "https://server.fillout.com/embed/v1/"
const FILLOUT_DEFAULT_DOMAIN = "forms.ahead.clinic"
const FILLOUT_SCRIPT_ORIGIN = "https://server.fillout.com"
const FILLOUT_FORM_ORIGIN = "https://forms.ahead.clinic"

/**
 * Tracks whether the Fillout embed script has loaded and executed at least
 * once in this JS context. Module-level so it survives component
 * unmount/remount (modal close→reopen, SPA navigation).
 */
let filloutScriptReady = false

export const FILLOUT_CALL_FORM_IDS: Record<string, string> = {
  en: "4EhxddUnbtus",
  de: "qgWi14wPgdus",
  fr: "5dHx6vpwE9us",
}

export function getFilloutCallFormId(locale: string): string {
  return FILLOUT_CALL_FORM_IDS[locale] ?? FILLOUT_CALL_FORM_IDS.en
}

/**
 * Open TCP/TLS connections to both Fillout origins early so the iframe + its
 * subresources don't pay handshake cost on first paint. Cheap (no data
 * transfer) and idempotent — React 19 dedupes the resulting <link> tags.
 *
 * Call from any page that *might* render a FilloutEmbed (e.g. /contact, where
 * the embed only mounts after the user opens the booking modal). For pages
 * that always render the embed, prefer `warmFillout()` which also preloads
 * the script.
 */
export function preconnectFillout() {
  preconnect(FILLOUT_SCRIPT_ORIGIN)
  preconnect(FILLOUT_FORM_ORIGIN)
}

/**
 * Same as `preconnectFillout()` plus a high-priority preload of the embed
 * script — use on pages where the embed is guaranteed to render (e.g.
 * /free-call) so the script download starts before hydration instead of
 * waiting for `afterInteractive`.
 */
export function warmFillout() {
  preconnectFillout()
  preload(FILLOUT_SCRIPT_SRC, { as: "script" })
}

interface FilloutEmbedProps {
  formId: string
  /** White-labeled domain override. Defaults to forms.ahead.clinic. */
  domain?: string
  /** Auto-resize the embed to match form content height. Default: true. */
  dynamicResize?: boolean
  /** Forward URL params from the host page into the form. Default: true. */
  inheritParameters?: boolean
  /** Additional URL params to pass to the form (key=value pairs). */
  parameters?: Record<string, string>
  /** Initial height while the form loads / if dynamicResize is off. */
  height?: number
  className?: string
  style?: React.CSSProperties
}

export function FilloutEmbed({
  formId,
  domain = FILLOUT_DEFAULT_DOMAIN,
  dynamicResize = true,
  inheritParameters = true,
  parameters,
  height = 500,
  className,
  style,
}: FilloutEmbedProps) {
  const paramsAttr = parameters
    ? new URLSearchParams(parameters).toString()
    : undefined
  const containerRef = useRef<HTMLDivElement>(null)
  const [iframeLoaded, setIframeLoaded] = useState(false)
  const [scriptFailed, setScriptFailed] = useState(false)

  // Warm the connection + start the script download as soon as the embed
  // renders. For pages that mount this conditionally (e.g. the /contact
  // booking modal), call `preconnectFillout()` at the page level too — that
  // gets the TCP/TLS handshake done before the user even clicks "Book".
  warmFillout()

  // The Fillout embed script runs a one-time querySelectorAll on load — no
  // MutationObserver, no re-init API. On remount (modal reopen, SPA nav),
  // the script is already loaded and won't re-scan. Detect this and
  // re-execute the script so it processes our new uninitialized div.
  useEffect(() => {
    if (!filloutScriptReady) return
    const container = containerRef.current
    if (!container || container.hasAttribute("data-fillout-initialized")) return
    const script = document.createElement("script")
    script.src = FILLOUT_SCRIPT_SRC
    script.addEventListener("load", () => script.remove(), { once: true })
    script.addEventListener(
      "error",
      () => {
        setScriptFailed(true)
        Sentry.captureMessage("[FilloutEmbed] Script re-exec failed", {
          level: "warning",
        })
        script.remove()
      },
      { once: true }
    )
    document.head.appendChild(script)
  }, [formId])

  // Watch for the iframe that the Fillout script injects into our container,
  // then listen for its `load` event so we can fade out the skeleton.
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleLoad = () => setIframeLoaded(true)

    const tryAttach = () => {
      const iframe = container.querySelector("iframe")
      if (!iframe) return false
      iframe.addEventListener("load", handleLoad, { once: true })
      return true
    }

    if (tryAttach()) return

    const observer = new MutationObserver(() => {
      if (tryAttach()) observer.disconnect()
    })
    observer.observe(container, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [formId])

  return (
    <div className={`relative ${className ?? ""}`} style={style}>
      <div
        ref={containerRef}
        data-fillout-id={formId}
        data-fillout-embed-type="standard"
        data-fillout-domain={domain}
        data-fillout-dynamic-resize={dynamicResize ? "" : undefined}
        data-fillout-inherit-parameters={inheritParameters ? "" : undefined}
        data-fillout-parameters={paramsAttr}
        style={{ width: "100%", height }}
      />
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 transition-opacity duration-300 ${
          iframeLoaded || scriptFailed ? "opacity-0" : "opacity-100"
        }`}
      >
        <FilloutSchedulerSkeleton />
      </div>
      {scriptFailed && (
        <div className="absolute inset-0 flex items-center justify-center bg-white p-6 text-center">
          <div>
            <p className="mb-3 text-neutral-600">
              The booking form could not be loaded.
            </p>
            <a
              href={`https://${domain}/${formId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-deep-blue-950 hover:bg-deep-blue-950/90 inline-block rounded-full px-6 py-3 text-sm font-medium text-white transition-colors"
            >
              Open booking form
            </a>
          </div>
        </div>
      )}
      <Script
        src={FILLOUT_SCRIPT_SRC}
        strategy="afterInteractive"
        onLoad={() => {
          filloutScriptReady = true
        }}
        onError={() => {
          setScriptFailed(true)
          Sentry.captureMessage("[FilloutEmbed] Failed to load embed script", {
            level: "warning",
          })
        }}
      />
    </div>
  )
}

/**
 * Scheduler-shaped skeleton: header, weekday row, calendar grid, and a
 * stacked list of time slots. Stacks vertically on mobile (<md), splits
 * into calendar + slots side-by-side on desktop. Background is white so it
 * matches the modal / card the embed sits inside, and the inner blocks
 * pulse with neutral-100/200 to read as a loading state.
 */
function FilloutSchedulerSkeleton() {
  return (
    <div className="h-full w-full bg-white p-4 sm:p-6 md:p-8">
      <div className="grid h-full grid-cols-1 gap-6 md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] md:gap-8">
        {/* Calendar */}
        <div className="flex animate-pulse flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="h-5 w-32 rounded bg-neutral-200" />
            <div className="flex gap-2">
              <div className="h-8 w-8 rounded-full bg-neutral-200" />
              <div className="h-8 w-8 rounded-full bg-neutral-200" />
            </div>
          </div>
          <div className="grid grid-cols-7 gap-2">
            {Array.from({ length: 7 }).map((_, i) => (
              <div
                key={i}
                className="mx-auto h-2.5 w-5 rounded bg-neutral-100"
              />
            ))}
          </div>
          <div className="grid grid-cols-7 gap-2">
            {Array.from({ length: 35 }).map((_, i) => (
              <div
                key={i}
                className="aspect-square rounded-md bg-neutral-100"
              />
            ))}
          </div>
        </div>
        {/* Time slot list */}
        <div className="flex animate-pulse flex-col gap-3">
          <div className="h-5 w-40 rounded bg-neutral-200" />
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-10 rounded-lg bg-neutral-100" />
          ))}
        </div>
      </div>
    </div>
  )
}
